/**
 * Single source of truth for the session credential.
 *
 * The token is sent as `Authorization: Bearer <token>`
 */

import { API_BASE_PATH, API_BACKEND_URL, FILE_BASE_PATH } from "../Config/Config";

const USER_DETAIL_KEY = "userDetail";

export const AUTH_HEADER = "Authorization";
export const AUTH_SCHEME = "Bearer";

/** Reads the persisted user, returning null rather than throwing on bad JSON. */
export const getStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_DETAIL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const getAuthToken = () => getStoredUser()?.token ?? null;

/**
 * Builds the auth header for a token. Pass an explicit token for credentials
 * that are not the logged-in session — public/shared report links, licence
 * keys. Returns an empty object when there is no token so it stays spreadable.
 *
 * Called with no arguments it uses the session token. Called with an explicit
 * argument it uses exactly that, and yields no header if it is empty — passing
 * `undefined` must not quietly substitute the session credential for a
 * different principal's.
 */
export const buildAuthHeader = (...args) => {
  const token = args.length > 0 ? args[0] : getAuthToken();
  return token ? { [AUTH_HEADER]: `${AUTH_SCHEME} ${token}` } : {};
};

const toOrigin = (value) => {
  try {
    return new URL(value, window.location.origin).origin;
  } catch {
    return null;
  }
};

/**
 * Origins allowed to receive the session token.
 *
 * FILE_BASE_PATH is separate from API_BASE_PATH in the deployed environments —
 * screenshots, log files and recordings come from their own host. Leave it out
 * and those requests go out with no credential at all.
 */
const TRUSTED_ORIGINS = new Set(
  [API_BASE_PATH, API_BACKEND_URL, FILE_BASE_PATH, window.location.origin]
    .filter(Boolean)
    .map(toOrigin)
    .filter(Boolean),
);

export const isTrustedApiUrl = (url) => {
  const origin = toOrigin(url ?? "");
  return origin !== null && TRUSTED_ORIGINS.has(origin);
};
